/**
 * Google Tag Manager Utility Functions
 *
 * These functions push events to Google Tag Manager's dataLayer
 * for tracking user interactions and conversions.
 */

import "./gtm-types";

/**
 * Initialize dataLayer if it doesn't exist
 */
const initDataLayer = () => {
  if (typeof window !== "undefined" && !window.dataLayer) {
    window.dataLayer = [];
  }
};

/**
 * Track which transaction IDs have already been pushed to prevent duplicate
 * purchase events (e.g. user refreshes the payment success page).
 * Google Ads uses orderId for server-side dedup, but GA4 and Meta Pixel
 * will still double-count without this client-side guard.
 */
const firedTransactions = new Set<string>();

/**
 * Generic function to push events to GTM dataLayer
 */
export const pushToDataLayer = (event: Record<string, unknown>) => {
  if (typeof window === "undefined") return;

  initDataLayer();

  try {
    window.dataLayer.push(event);
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log("[GTM] Event pushed:", event);
    }
  } catch (error) {
    console.error("[GTM] Failed to push event:", error);
  }
};

/**
 * Track when user starts AI resume generation
 */
export const trackGenerateResumeStart = (data: {
  jobTitle: string;
  experienceLevel: string;
  language: string;
  country: string;
  tone: string;
}) => {
  pushToDataLayer({
    event: "generate_resume_start",
    jobTitle: data.jobTitle,
    experienceLevel: data.experienceLevel,
    language: data.language,
    country: data.country,
    tone: data.tone,
  });
};

/**
 * Track when AI resume generation completes successfully
 */
export const trackGenerateResumeComplete = (data: {
  jobTitle: string;
  generationTime: number; // in seconds
  tokensUsed?: number;
}) => {
  pushToDataLayer({
    event: "generate_resume_complete",
    jobTitle: data.jobTitle,
    generationTime: data.generationTime,
    tokensUsed: data.tokensUsed,
  });
};

/**
 * Track when AI resume generation fails
 */
export const trackGenerateResumeFailed = (data: { jobTitle: string; errorMessage: string }) => {
  pushToDataLayer({
    event: "generate_resume_failed",
    jobTitle: data.jobTitle,
    errorMessage: data.errorMessage,
  });
};

/**
 * Track when user extracts job URL (costs 30 tokens)
 */
export const trackExtractJobUrl = (data: { jobUrl: string; tokensUsed?: number }) => {
  pushToDataLayer({
    event: "extract_job_url",
    jobUrl: data.jobUrl,
    tokensUsed: data.tokensUsed || 30,
  });
};

/**
 * Track sign-up
 */
export const trackSignUp = (data: { method?: string }) => {
  pushToDataLayer({
    event: "sign_up",
    method: data.method ?? "email",
  });
};

/**
 * Track login
 */
export const trackLogin = (data: { method?: string }) => {
  pushToDataLayer({
    event: "login",
    method: data.method ?? "email",
  });
};

/**
 * Track when user selects a resume template
 */
export const trackTemplateSelected = (data: {
  templateName: string;
  templateCategory?: string;
  source?: "generator" | "templates_page" | "builder";
}) => {
  pushToDataLayer({
    event: "select_template",
    templateName: data.templateName,
    templateCategory: data.templateCategory,
    source: data.source,
  });
};

/** Outcome when user attempts download: success (PDF delivered) or redirect type */
export type DownloadResumeOutcome = "success" | "redirect_to_signup" | "redirect_to_pricing" | "blocked_free_limit";

/**
 * Track when user downloads/exports resume (or attempts and is redirected)
 */
export const trackDownloadResume = (data: {
  resumeId: string;
  format: "pdf" | "docx" | "json";
  templateName?: string;
  outcome?: DownloadResumeOutcome;
}) => {
  pushToDataLayer({
    event: "download_resume",
    resumeId: data.resumeId,
    resumeFormat: data.format,
    templateName: data.templateName,
    outcome: data.outcome ?? "success",
  });
};

/**
 * Track language change
 */
export const trackLanguageChange = (data: { fromLanguage: string; toLanguage: string }) => {
  pushToDataLayer({
    event: "language_change",
    fromLanguage: data.fromLanguage,
    toLanguage: data.toLanguage,
  });
};

/**
 * Track country selection
 */
export const trackCountrySelect = (data: { country: string; source?: string }) => {
  pushToDataLayer({
    event: "country_select",
    country: data.country,
    source: data.source,
  });
};

/**
 * Track form abandonment
 */
export const trackFormAbandon = (data: {
  formName: string;
  step: string;
  fieldsCompleted?: number;
}) => {
  pushToDataLayer({
    event: "form_abandon",
    formName: data.formName,
    formStep: data.step,
    fieldsCompleted: data.fieldsCompleted,
  });
};

/**
 * Track when user begins checkout (clicks purchase button)
 */
export const trackBeginCheckout = (data: {
  value: number;
  currency: string;
  items: {
    item_id: string;
    item_name: string;
    item_category?: string;
    price: number;
    quantity: number;
  }[];
}) => {
  pushToDataLayer({
    event: "begin_checkout",
    // Flat values for GTM Data Layer Variables (DLV - value, DLV - currency)
    value: data.value,
    currency: data.currency,
    // Nested ecommerce object for GA4 Enhanced Ecommerce
    ecommerce: {
      currency: data.currency,
      value: data.value,
      items: data.items,
    },
  });
};

/**
 * Track successful purchase (tokens or subscription)
 */
export const trackPurchase = (data: {
  transaction_id: string;
  value: number;
  currency: string;
  items: {
    item_id: string;
    item_name: string;
    item_category?: string;
    price: number;
    quantity: number;
  }[];
  coupon?: string;
}) => {
  pushToDataLayer({
    event: "purchase",
    transaction_id: data.transaction_id,
    value: data.value,
    currency: data.currency,
    items: data.items,
    coupon: data.coupon,
  });
};

/**
 * Track subscription purchase specifically
 */
export const trackSubscriptionPurchase = (data: {
  transaction_id: string;
  subscription_id?: string;
  plan_name: string;
  plan_value: number;
  currency: string;
  billing_period: "monthly" | "yearly";
  provider?: "stripe" | "paddle" | "fastspring" | "twocheckout";
}) => {
  // Prevent duplicate purchase events on page refresh
  if (firedTransactions.has(data.transaction_id)) {
    if (import.meta.env.DEV) {
      console.log("[GTM] Skipping duplicate purchase event for:", data.transaction_id);
    }
    return;
  }
  firedTransactions.add(data.transaction_id);

  pushToDataLayer({
    event: "purchase",
    transaction_id: data.transaction_id,
    value: data.plan_value,
    currency: data.currency,
    plan: data.plan_name,
    provider: data.provider ?? "stripe",
    items: [
      {
        item_id: data.subscription_id || data.transaction_id,
        item_name: data.plan_name,
        item_category: "subscription",
        price: data.plan_value,
        quantity: 1,
      },
    ],
    subscription_id: data.subscription_id,
    billing_period: data.billing_period,
  });
};

/**
 * Track token purchase specifically
 */
export const trackTokenPurchase = (data: {
  transaction_id: string;
  tokens_purchased: number;
  amount_paid: number;
  currency: string;
  package_name?: string;
}) => {
  // Prevent duplicate purchase events on page refresh
  if (firedTransactions.has(data.transaction_id)) {
    if (import.meta.env.DEV) {
      console.log("[GTM] Skipping duplicate token purchase event for:", data.transaction_id);
    }
    return;
  }
  firedTransactions.add(data.transaction_id);

  pushToDataLayer({
    event: "purchase",
    transaction_id: data.transaction_id,
    value: data.amount_paid,
    currency: data.currency,
    provider: "stripe",
    items: [
      {
        item_id: data.package_name || `tokens_${data.tokens_purchased}`,
        item_name: `${data.tokens_purchased} AI Tokens`,
        item_category: "tokens",
        price: data.amount_paid,
        quantity: 1,
      },
    ],
    tokens_purchased: data.tokens_purchased,
  });
};

// ──────────────────────────────────────────────────────────
// Pageview tracking (replaces direct gtag("config") calls)
// ──────────────────────────────────────────────────────────

/**
 * Track virtual pageview via dataLayer
 */
export const trackPageview = (url: string) => {
  pushToDataLayer({
    event: "page_view",
    page_path: url,
  });
};

// ──────────────────────────────────────────────────────────
// Resume lifecycle events
// ──────────────────────────────────────────────────────────

export const trackResumeEvent = {
  generation: (template?: string) => {
    pushToDataLayer({ event: "generate_resume", template });
  },
  export: (format: "pdf" | "docx" | "json") => {
    pushToDataLayer({ event: "export_resume", format });
  },
  templateSelected: (templateName: string) => {
    pushToDataLayer({ event: "select_template", templateName });
  },
  saved: () => {
    pushToDataLayer({ event: "save_resume" });
  },
  updated: () => {
    pushToDataLayer({ event: "update_resume" });
  },
  created: () => {
    pushToDataLayer({ event: "create_resume" });
  },
  shared: (platform?: string) => {
    pushToDataLayer({ event: "share_resume", sharePlatform: platform, shareContext: platform ? "post_download" : "sharing_section" });
  },
};

// ──────────────────────────────────────────────────────────
// AI feature events
// ──────────────────────────────────────────────────────────

export const trackAIEvent = {
  summaryGenerated: () => {
    pushToDataLayer({ event: "generate_summary", category: "ai_features" });
  },
  coverLetterGenerated: () => {
    pushToDataLayer({ event: "generate_cover_letter", category: "ai_features" });
  },
  boosterUsed: () => {
    pushToDataLayer({ event: "use_booster", category: "ai_features" });
  },
  atsAnalysis: (score?: number) => {
    pushToDataLayer({ event: "ats_analysis", category: "ai_features", score });
  },
  jobFitScore: (score?: number) => {
    pushToDataLayer({ event: "job_fit_score", category: "ai_features", score });
  },
  improveWriting: () => {
    pushToDataLayer({ event: "improve_writing", category: "ai_features" });
  },
  fixSpellingGrammar: () => {
    pushToDataLayer({ event: "fix_spelling_grammar", category: "ai_features" });
  },
  changeTone: (mood: string) => {
    pushToDataLayer({ event: "change_tone", category: "ai_features", mood });
  },
};

// ──────────────────────────────────────────────────────────
// User events
// ──────────────────────────────────────────────────────────

export const trackUserEvent = {
  profileCompleted: () => {
    pushToDataLayer({ event: "complete_profile", category: "user" });
  },
};

// ──────────────────────────────────────────────────────────
// Payment success (single event for GTM "Payment Successful" tag)
// ──────────────────────────────────────────────────────────

/**
 * Fire once when user lands on payment success page and payment is confirmed.
 * Use this in GTM to trigger a "Payment Successful" or conversion tag.
 */
export const trackPaymentSuccess = (data?: {
  type?: "subscription" | "tokens";
  provider?: "stripe" | "paddle" | "fastspring" | "twocheckout";
  value?: number;
  currency?: string;
  transaction_id?: string;
}) => {
  // Deduplicate by transaction_id if available
  if (data?.transaction_id && firedTransactions.has(`ps_${data.transaction_id}`)) {
    return;
  }
  if (data?.transaction_id) {
    firedTransactions.add(`ps_${data.transaction_id}`);
  }

  pushToDataLayer({
    event: "payment_success",
    page_path: typeof window !== "undefined" ? window.location.pathname + window.location.search : "",
    ...data,
  });
};

// ──────────────────────────────────────────────────────────
// Payment failure & cancellation tracking
// ──────────────────────────────────────────────────────────

export type PaymentProvider = "stripe" | "paddle" | "fastspring" | "twocheckout";
export type PaymentErrorType =
  | "card_declined"
  | "confirmation_failed"
  | "authentication_required" // 3DS/SCA required or failed — segment separately from real declines
  | "network_error"
  | "provider_load_failed"
  | "payment_method_failed"
  | "checkout_session_failed"
  | "unknown";

/**
 * Track when a payment attempt fails
 */
export const trackPaymentFailed = (data: {
  provider: PaymentProvider;
  error_type: PaymentErrorType;
  error_message: string;
  payment_method?: string;
  value?: number;
  currency?: string;
}) => {
  pushToDataLayer({
    event: "payment_failed",
    provider: data.provider,
    error_type: data.error_type,
    error_message: data.error_message,
    payment_method: data.payment_method,
    value: data.value,
    currency: data.currency,
  });
};

/**
 * Track when user cancels checkout (closes popup, navigates to cancel page, etc.)
 */
export const trackCheckoutCancelled = (data: {
  provider?: PaymentProvider;
  stage?: string;
}) => {
  pushToDataLayer({
    event: "checkout_cancelled",
    provider: data.provider,
    stage: data.stage,
  });
};

// ──────────────────────────────────────────────────────────
// Payment recovery funnel tracking
// ──────────────────────────────────────────────────────────

/**
 * Track when a recovery suggestion is shown to the user after payment failure
 */
export const trackRecoveryShown = (data: {
  provider: string;
  suggestion_type: string;
  waterfall_step: number;
  decline_code?: string;
  original_provider?: string;
  original_tier?: string;
}) => {
  pushToDataLayer({
    event: "recovery_shown",
    ...data,
  });
};

/**
 * Track when user accepts a recovery suggestion
 */
export const trackRecoveryAccepted = (data: {
  provider: string;
  suggestion_type: string;
  waterfall_step: number;
  decline_code?: string;
  original_provider?: string;
  original_tier?: string;
}) => {
  pushToDataLayer({
    event: "recovery_accepted",
    ...data,
  });
};

/**
 * Track when user skips a recovery suggestion (advances waterfall)
 */
export const trackRecoverySkipped = (data: {
  provider: string;
  suggestion_type: string;
  waterfall_step: number;
  decline_code?: string;
}) => {
  pushToDataLayer({
    event: "recovery_skipped",
    ...data,
  });
};

/**
 * Track when yearly->monthly downsell is shown after insufficient funds.
 */
export const trackDownsellShown = (data: {
  from_billing_cycle: "yearly";
  to_billing_cycle: "monthly";
  decline_code: string;
  from_price_id?: string;
  to_price_id?: string;
}) => {
  pushToDataLayer({
    event: "downsell_shown",
    ...data,
  });
};

/**
 * Track when user accepts yearly->monthly downsell.
 */
export const trackDownsellAccepted = (data: {
  from_billing_cycle: "yearly";
  to_billing_cycle: "monthly";
  decline_code: string;
  from_price_id?: string;
  to_price_id?: string;
}) => {
  pushToDataLayer({
    event: "downsell_accepted",
    ...data,
  });
};

// ──────────────────────────────────────────────────────────
// Generic event push (for one-off tracking)
// ──────────────────────────────────────────────────────────

export const trackEvent = (data: {
  action: string;
  category: string;
  label?: string;
  value?: number;
}) => {
  pushToDataLayer({
    event: data.action,
    event_category: data.category,
    event_label: data.label,
    value: data.value,
  });
};
